import {
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfDay,
  format,
  startOfDay,
  startOfWeek,
} from 'date-fns';

// Daily/weekly bucketing, shared across all activity charts. The time range
// (start/end) and granularity are controlled globally in ListingActivity.
export type Granularity = 'day' | 'week';

const bucketStart = (date: Date, granularity: Granularity): Date =>
  granularity === 'day'
    ? startOfDay(date)
    : startOfWeek(date, { weekStartsOn: 1 });

export type Buckets = {
  /** ISO start key per bucket, used to aggregate items. */
  keys: string[];
  /** Human labels per bucket for the x-axis. */
  labels: string[];
  /** Maps an item date to its bucket key. */
  keyOf: (date: Date) => string;
  /** First instant in the selected range — items before this are excluded. */
  start: Date;
  /** Last instant in range — items after this are excluded. */
  end: Date;
};

// Build a continuous, zero-fillable set of buckets across [start, end] so the
// charts always show the full timeline (including empty buckets as 0s).
export const buildBuckets = (
  start: Date,
  end: Date,
  granularity: Granularity,
): Buckets => {
  const rangeStart = startOfDay(start);
  const first = bucketStart(rangeStart, granularity);
  const last = endOfDay(end);
  const dates =
    granularity === 'day'
      ? eachDayOfInterval({ start: first, end: last })
      : eachWeekOfInterval({ start: first, end: last }, { weekStartsOn: 1 });
  const keyOf = (date: Date) =>
    format(bucketStart(date, granularity), 'yyyy-MM-dd');
  return {
    keys: dates.map(keyOf),
    labels: dates.map(date =>
      format(date < rangeStart ? rangeStart : date, 'd MMM'),
    ),
    keyOf,
    start: rangeStart,
    end: last,
  };
};

export type StackedSeries = { label: string; data: number[] };

// Count timestamped items into stacked-by-category series over the buckets.
// Categories keep insertion order so legend colors stay stable.
export const aggregateStacked = <T>(
  items: readonly T[],
  getDate: (item: T) => Date,
  getCategory: (item: T) => string,
  buckets: Buckets,
): StackedSeries[] => {
  const order: string[] = [];
  const counts = new Map<string, Map<string, number>>();
  for (const item of items) {
    const date = getDate(item);
    if (date < buckets.start || date > buckets.end) {
      continue;
    }
    const category = getCategory(item);
    const key = buckets.keyOf(date);
    let perBucket = counts.get(category);
    if (perBucket == null) {
      perBucket = new Map();
      counts.set(category, perBucket);
      order.push(category);
    }
    perBucket.set(key, (perBucket.get(key) ?? 0) + 1);
  }
  return order.map(category => ({
    label: category,
    data: buckets.keys.map(key => counts.get(category)?.get(key) ?? 0),
  }));
};

// Distinct, reasonably accessible hues cycled per series.
export const SERIES_COLORS = [
  '#4285f4',
  '#5e35b1',
  '#00897b',
  '#e8710a',
  '#c2185b',
  '#3949ab',
  '#00acc1',
  '#7cb342',
];
